<template>
  <div class="about-page">

    <!-- ═══════════════════════════ HERO ═══════════════════════════ -->
    <section class="about-hero">
      <div class="about-hero-inner">
        <p class="section-eyebrow">About NexaScale</p>
        <h1 class="about-h1">
          Making <span class="about-h1-grad">work experience</span><br/>
          and career growth accessible<br/>
          to everyone.
        </h1>
      </div>
    </section>

    <!-- ═══════════════════════════ MISSION ═══════════════════════════ -->
    <section class="mission-section">
      <div class="mission-inner">
        <div class="mission-left">
          <p class="section-eyebrow">Our mission</p>
          <h2 class="mission-h2">
            To build a community where people<br/>
            can <em>connect and grow</em><br/>
            with each other.
          </h2>
        </div>
        <div class="mission-right">
          <p class="mission-body">
            We are a nonprofit committed to fostering the growth and development of the next 
            generation of tech professionals. We believe that by providing resources and 
            opportunities for learning and skill building, we can help individuals achieve 
            their full potential and contribute to the growth of the tech industry.
          </p>
          <p class="mission-body">
            Through our partnerships with tech companies and organizations, we provide 
            internships, job opportunities, and career advancement support to our members. 
            By collaborating with industry leaders, we help our members gain practical 
            experience and access to valuable networks.
          </p>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════ GET INVOLVED ═══════════════════════════ -->
    <section class="involved-section">
      <div class="involved-inner">
        <p class="section-eyebrow">Get involved</p>
        <h2 class="involved-h2">Three ways to be part of the change.</h2>
        <div class="involved-grid">

          <div class="involved-card involved-card--dark">
            <div class="involved-card-num">01</div>
            <h3 class="involved-card-title">Join our Slack community</h3>
            <p class="involved-card-body">
              Become part of a vibrant network of builders, learners, and tech professionals 
              passionate about growing together. Access resources, opportunities, and people 
              who actually show up for you.
            </p>
            <a class="involved-btn involved-btn--yellow" href="https://forms.gle/nivXcK3MwJ8ZeFRp7" target="_blank">
              Join us →
            </a>
          </div>

          <div class="involved-card involved-card--yellow">
            <div class="involved-card-num involved-card-num--dark">02</div>
            <h3 class="involved-card-title involved-card-title--dark">Partner with us</h3>
            <p class="involved-card-body involved-card-body--dark">
              Whether you're a tech company, nonprofit, or university — partnering with NexaScale 
              means investing in the next generation of talent. Together we can create real 
              pathways into the industry.
            </p>
            <a class="involved-btn involved-btn--dark" href="mailto:info@nexascale.org" target="_blank">
              Partner with us →
            </a>
          </div>

          <div class="involved-card involved-card--light">
            <div class="involved-card-num involved-card-num--dark">03</div>
            <h3 class="involved-card-title involved-card-title--dark">Donate</h3>
            <p class="involved-card-body involved-card-body--dark">
              Every donation closes a gap — whether it's access to a laptop, a scholarship, 
              or a program that changes someone's trajectory. Your contribution goes directly 
              toward building equitable tech careers.
            </p>
            <router-link class="involved-btn involved-btn--filled" to="/donate">
              Donate →
            </router-link>
          </div>

        </div>
      </div>
    </section>

    <!-- ═══════════════════════════ CORE TEAM ═══════════════════════════ -->
    <section class="team-section">
      <div class="team-inner">
        <div class="team-header">
          <p class="section-eyebrow">The people behind it</p>
          <h2 class="team-h2">Core Team</h2>
        </div>
        <div class="team-grid">

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/adora.jpg" alt="Adora Nwodo" />
            </div>
            <p class="team-name">Adora Nwodo</p>
            <p class="team-role">Founder</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/adoranwodo" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
              <a href="https://twitter.com/adoranwodo" target="_blank"><font-awesome-icon icon="fa-brands fa-twitter" /></a>
              <a href="https://adoranwodo.com" target="_blank"><font-awesome-icon icon="fa-solid fa-globe" /></a>
            </div>
          </div>

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/bukola-min.jpg" alt="Bukola Bisuga" />
            </div>
            <p class="team-name">Bukola Bisuga</p>
            <p class="team-role">Head of Partnerships</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/bukola-bisuga/" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
              <a href="https://twitter.com/bukolabisuga" target="_blank"><font-awesome-icon icon="fa-brands fa-twitter" /></a>
            </div>
          </div>

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/busayo-min.jpeg" alt="Busayo Bamigbade" />
            </div>
            <p class="team-name">Busayo Bamigbade</p>
            <p class="team-role">Operations Manager</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/oluwabusayomi-bamigbade-4187b023b/" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
            </div>
          </div>

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/celeste-min.jpeg" alt="Celeste Salopek" />
            </div>
            <p class="team-name">Celeste Salopek</p>
            <p class="team-role">Program Manager</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/csalo/" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
              <a href="https://twitter.com/plainjanedoe12" target="_blank"><font-awesome-icon icon="fa-brands fa-twitter" /></a>
            </div>
          </div>

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/fatimah-b-min.jpg" alt="Fatimah Badmus" />
            </div>
            <p class="team-name">Fatimah Badmus</p>
            <p class="team-role">Lead Community Manager</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/fatimah-badmus-938991b9/" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
              <a href="https://twitter.com/OlaideBadmus_" target="_blank"><font-awesome-icon icon="fa-brands fa-twitter" /></a>
            </div>
          </div>

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/bisola-min.jpg" alt="Bisola Taiwo" />
            </div>
            <p class="team-name">Bisola Taiwo</p>
            <p class="team-role">Content Strategist</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/taiwo-bisola-bb2437235/" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
            </div>
          </div>

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/nneoma.jpeg" alt="Nneoma Obinyeluba" />
            </div>
            <p class="team-name">Nneoma Obinyeluba</p>
            <p class="team-role">Operations Coordinator</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/favour-obinyeluba-2360b7244/" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
            </div>
          </div>

          <div class="team-member">
            <div class="team-member-img-wrap">
              <img src="../assets/images/core-team/sarah.jpeg" alt="Sarah Paul" />
            </div>
            <p class="team-name">Sarah Paul</p>
            <p class="team-role">Brand Manager</p>
            <div class="team-links">
              <a href="https://www.linkedin.com/in/sarah-chika-paul/" target="_blank"><font-awesome-icon icon="fa-brands fa-linkedin" /></a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════════════════════ VOLUNTEERS ═══════════════════════════ -->
    <section class="volunteers-section">
      <div class="team-inner">
        <div class="team-header">
          <p class="section-eyebrow">Powered by community</p>
          <h2 class="team-h2">Volunteers</h2>
        </div>
        <div class="team-grid team-grid--volunteers">
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/eunice.jpeg" alt="Eunice Adediran" /></div>
            <p class="team-name">Eunice Adediran</p>
            <p class="team-role">Program Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/joshua-min.jpg" alt="Joshua Abel" /></div>
            <p class="team-name">Joshua Abel</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/ezinne-min.jpg" alt="Ezinne Jennifer Aruma" /></div>
            <p class="team-name">Ezinne Jennifer Aruma</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/victoria-min.jpg" alt="Braimoh Victoria" /></div>
            <p class="team-name">Braimoh Victoria</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/ameh-min.jpg" alt="Ameh Ugbede" /></div>
            <p class="team-name">Ameh Ugbede</p>
            <p class="team-role">Designer</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/feranmi-min.jpg" alt="Feranmi Falana" /></div>
            <p class="team-name">Feranmi Falana</p>
            <p class="team-role">Designer</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/ofon-min.jpg" alt="Ofonime Brown" /></div>
            <p class="team-name">Ofonime Brown</p>
            <p class="team-role">Program Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/peaceo.jpeg" alt="Peace Onyekachi" /></div>
            <p class="team-name">Peace Onyekachi</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/david.jpg" alt="David Adurotimi" /></div>
            <p class="team-name">David Adurotimi</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/olaitan.jpg" alt="Olaitan Adedokun" /></div>
            <p class="team-name">Olaitan Adedokun</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/ope-kareem-min.jpg" alt="Ope Kareem" /></div>
            <p class="team-name">Ope Kareem</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/chiamaka.jpeg" alt="Chiamaka Grace Anyanwu" /></div>
            <p class="team-name">Chiamaka Grace Anyanwu</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/audrey.jpeg" alt="Audrey Zunuoh Ponu" /></div>
            <p class="team-name">Audrey Zunuoh Ponu</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/somkene.jpeg" alt="Somkenechukwu Odikpo" /></div>
            <p class="team-name">Somkenechukwu Odikpo</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/peace.jpeg" alt="Peace Sandy" /></div>
            <p class="team-name">Peace Sandy</p>
            <p class="team-role">Technical Writer</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/gideon.jpeg" alt="Gideon John Utong" /></div>
            <p class="team-name">Gideon John Utong</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/godsfavour.jpeg" alt="Ezeliora Godsfavour" /></div>
            <p class="team-name">Ezeliora Godsfavour</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/pricilia.jpg" alt="Pricilia Ighodaro" /></div>
            <p class="team-name">Pricilia Ighodaro</p>
            <p class="team-role">Designer</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/chidiebere.jpeg" alt="Chidiebere Anabaraonye" /></div>
            <p class="team-name">Chidiebere Anabaraonye</p>
            <p class="team-role">Designer</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/chinanza.jpg" alt="Chinaza Blossom" /></div>
            <p class="team-name">Chinaza Blossom</p>
            <p class="team-role">Community Manager</p>
          </div>
          <div class="team-member">
            <div class="team-member-img-wrap"><img src="../assets/images/volunteers/dami.jpeg" alt="Oluwadamilare Oyelakin" /></div>
            <p class="team-name">Oluwadamilare Oyelakin</p>
            <p class="team-role">Community Manager</p>
          </div>

        </div>
      </div>
    </section>

    <!-- ═══════════════════════════ PARTNERS ═══════════════════════════ -->
    <section class="partners-section">
      <div class="partners-inner">
        <p class="section-eyebrow">Our Partners</p>
        <h2 class="partners-h2">Backed by organisations<br/>that believe in access.</h2>
        <div class="partners-grid">
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/landmark.jpg" alt="Landmark" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/moniepointlogo.png" alt="Moniepoint" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/selar-logo.svg" alt="Selar" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/miva-blue-logo.png" alt="Miva" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/thenest.png" alt="The Nest" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/csf4.png" alt="CSF4" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/hervest.png" alt="Hervest" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/wetechng.png" alt="WeTech" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/deimos.png" alt="Deimos" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/colab.png" alt="Co.Lab" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/accrue.webp" alt="Accrue" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/pipeops.png" alt="PipeOps" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/genesys.png" alt="Genesys" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/women-tech.png" alt="Women in Tech" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/mantahq.png" alt="MantaHQ" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/devslam.jpg" alt="DevSlam" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/codematic.png" alt="Codematic" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/gitex.png" alt="Gitex" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/kd.jpeg" alt="KD" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/alt-school.png" alt="AltSchool" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/owyla.png" alt="Owyla" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/condia.png" alt="Condia" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/flutterbytes.jpg" alt="FlutterBytes" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/techcabal.png" alt="TechCabal" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/techsafe.jpeg" alt="TechSafe" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/thegrowthhub.jpg" alt="The Growth Hub" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/wtec.png" alt="WTEC" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/wtech.png" alt="WTech" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/yellowlyfe.png" alt="Yellowlyfe" /></div>
          <div class="partner-cell"><img class="partner-logo" src="../assets/images/partners/alvative.jpeg" alt="Alvative" /></div>
        </div>
      </div>
    </section>

  </div>
</template>

<script>
export default {
  name: 'AboutView'
}
</script>

<style scoped>
/* ════════════════════════════════════════
   RESET
════════════════════════════════════════ */
.about-page {
  min-height: var(--pageheight, 100vh);
  margin-top: 80px;
  text-align: left;
}
.about-page * { box-sizing: border-box; }
.about-page p, .about-page h1, .about-page h2,
.about-page h3, .about-page ul { text-align: left; }

/* ════════════════════════════════════════
   SHARED
════════════════════════════════════════ */
.section-eyebrow {
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: #FFB812;
  margin-bottom: 14px;
  display: block;
}

/* ════════════════════════════════════════
   HERO
════════════════════════════════════════ */
.about-hero {
  background: #191616;
  padding: 100px 7% 90px;
}
.about-hero-inner {
  max-width: 1280px;
  margin: 0 auto;
}
.about-h1 {
  font-size: clamp(2.4rem, 5.5vw, 4.8rem);
  font-weight: 800;
  line-height: 1.05;
  letter-spacing: -0.03em;
  color: #ffffff;
  margin: 0;
  max-width: 100%;
  word-break: break-word;
}
.about-h1-grad {
  background: linear-gradient(90deg, #FFB812 0%, #FF9914 60%, #FF6B00 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

/* ════════════════════════════════════════
   MISSION
════════════════════════════════════════ */
.mission-section {
  padding: 100px 7%;
  background: #ffffff;
  border-bottom: 1px solid #e8e4e0;
}
.mission-inner {
  max-width: 1280px;
  margin: 0 auto;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 80px;
  align-items: start;
}
.mission-h2 {
  font-size: clamp(1.8rem, 3vw, 2.6rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.15;
  color: #191616;
  margin: 0;
}
.mission-h2 em {
  font-style: normal;
  background: linear-gradient(90deg, #FF9914, #FFB812);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}
.mission-body {
  font-size: 0.97rem;
  line-height: 1.8;
  color: #555250;
  margin: 0 0 20px;
}
.mission-body:last-child { margin-bottom: 0; }

/* ════════════════════════════════════════
   GET INVOLVED
════════════════════════════════════════ */
.involved-section {
  padding: 100px 7%;
  background: #FAFAF7;
}
.involved-inner {
  max-width: 1280px;
  margin: 0 auto;
}
.involved-h2 {
  font-size: clamp(1.8rem, 4vw, 3rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  line-height: 1.1;
  color: #191616;
  margin-bottom: 52px;
  max-width: 100%;
  word-break: break-word;
}
.involved-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
}
.involved-card {
  border-radius: 20px;
  padding: 40px 36px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}
.involved-card--dark  { background: #191616; }
.involved-card--yellow { background: #FFB812; }
.involved-card--light {
  background: #ffffff;
  border: 1.5px solid #e8e4e0;
}
.involved-card-num {
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.16em;
  color: #FFB812;
}
.involved-card-num--dark { color: rgba(25,22,22,0.45); }
.involved-card-title {
  font-size: 1.3rem;
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #ffffff;
  margin: 0;
  line-height: 1.2;
}
.involved-card-title--dark { color: #191616; }
.involved-card-body {
  font-size: 0.92rem;
  line-height: 1.75;
  color: rgba(255,255,255,0.6);
  margin: 0;
  flex: 1;
}
.involved-card-body--dark { color: rgba(25,22,22,0.65); }

/* Light card needs explicit dark text since default is white */
.involved-card--light .involved-card-num,
.involved-card--light .involved-card-title,
.involved-card--light .involved-card-body {
  color: #191616;
}
.involved-card--light .involved-card-num { color: #FFB812; }
.involved-card--light .involved-card-body { color: #555250; }
.involved-btn {
  display: inline-block;
  padding: 11px 22px;
  border-radius: 8px;
  font-size: 0.85rem;
  font-weight: 700;
  text-decoration: none;
  align-self: flex-start;
  transition: background 0.2s, color 0.2s;
}
.involved-btn--yellow {
  background: #FFB812;
  color: #191616;
}
.involved-btn--yellow:hover { background: #FF9914; }
.involved-btn--dark {
  background: #191616;
  color: #FFB812;
}
.involved-btn--dark:hover { background: #333; }
.involved-btn--filled {
  background: #191616;
  color: #ffffff;
}
.involved-btn--filled:hover { background: #333; }

/* ════════════════════════════════════════
   TEAM SHARED
════════════════════════════════════════ */
.team-section {
  padding: 100px 7%;
  background: #ffffff;
}
.volunteers-section {
  padding: 100px 7%;
  background: #FAFAF7;
}
.team-inner {
  max-width: 1280px;
  margin: 0 auto;
}
.team-header {
  margin-bottom: 56px;
  padding-bottom: 32px;
  border-bottom: 1px solid #e8e4e0;
}
.team-h2 {
  font-size: clamp(2rem, 3.5vw, 2.8rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #191616;
  margin: 0;
}
.team-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px 28px;
}
.team-grid--volunteers {
  grid-template-columns: repeat(6, 1fr);
  gap: 32px 20px;
}
.team-member { display: flex; flex-direction: column; gap: 6px; }
.team-member-img-wrap {
  width: 100%;
  aspect-ratio: 1;
  border-radius: 16px;
  overflow: hidden;
  margin-bottom: 10px;
  background: #f0ece8;
}
.team-member-img-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: top;
  display: block;
  transition: transform 0.3s;
}
.team-member:hover .team-member-img-wrap img {
  transform: scale(1.04);
}
/* Volunteers get circular images */
.team-grid--volunteers .team-member-img-wrap {
  border-radius: 50%;
}
.team-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #191616;
  margin: 0;
  line-height: 1.3;
}
.team-role {
  font-size: 0.78rem;
  color: #888480;
  margin: 0;
  font-weight: 500;
}
.team-links {
  display: flex;
  gap: 10px;
  margin-top: 4px;
}
.volunteers-section .team-name, .volunteers-section .team-role{
    text-align: center !important;
}
.team-links a {
  color: #c0bbb8;
  font-size: 0.9rem;
  text-decoration: none;
  transition: color 0.2s;
}
.team-links a:hover { color: #FFB812; }

/* ════════════════════════════════════════
   PARTNERS
════════════════════════════════════════ */
.partners-section {
  padding: 100px 7%;
  background: #ffffff;
  border-top: 1px solid #e8e4e0;
}
.partners-inner {
  max-width: 1280px;
  margin: 0 auto;
}
.partners-h2 {
  font-size: clamp(1.6rem, 3vw, 2.4rem);
  font-weight: 800;
  letter-spacing: -0.02em;
  color: #191616;
  margin-bottom: 60px;
  line-height: 1.2;
}
.partners-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 0;
  border-top: 1px solid #e0dcd8;
  border-left: 1px solid #e0dcd8;
}
.partner-cell {
  width: 16.666%;
  border-right: 1px solid #e0dcd8;
  border-bottom: 1px solid #e0dcd8;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 24px;
  min-height: 90px;
}
.partner-logo {
  width: 100%;
  max-width: 130px;
  height: auto;
  max-height: 60px;
  object-fit: contain;
  filter: grayscale(1);
  opacity: 0.6;
  transition: filter 0.2s, opacity 0.2s;
}
.partner-cell:hover .partner-logo {
  filter: grayscale(0);
  opacity: 1;
}

/* ════════════════════════════════════════
   RESPONSIVE
════════════════════════════════════════ */
@media (max-width: 1024px) {
  .mission-inner { grid-template-columns: 1fr; gap: 40px; }
  .involved-grid { grid-template-columns: 1fr; gap: 16px; }
  .team-grid { grid-template-columns: repeat(3, 1fr); }
  .team-grid--volunteers { grid-template-columns: repeat(4, 1fr); }
  .partner-cell { width: 25%; }
}
@media (max-width: 768px) {
  .about-hero, .mission-section, .involved-section,
  .team-section, .volunteers-section, .partners-section {
    padding-left: 5%;
    padding-right: 5%;
  }
  .team-grid { grid-template-columns: repeat(2, 1fr); }
  .team-grid--volunteers { grid-template-columns: repeat(3, 1fr); }
  .partner-cell { width: 33.333%; }
}
@media (max-width: 480px) {
  .team-grid--volunteers { grid-template-columns: repeat(2, 1fr); }
  .partner-cell { width: 50%; }
}
</style>